<template lang="pug">
.select-filter.form-field.form-multiselect.filter(:class="filterClass")
  .select-filter__label(v-if="showSelectionLabel") {{ label }}
  multiselect.multiple-select-filter-field(
    v-model="currentFilter",
    track-by="value",
    label="label",
    :disabled="disabled",
    :multiple="multiple",
    :placeholder="placeholder",
    :show-labels="false",
    @search-change="searchFn",
    :options="filterOptions",
    :closeOnSelect="!multiple",
    :hideSelected="true",
    @open="multiselectHandler",
    open-direction="bottom"
  )
    span(slot="noResult")
      i {{ notifies.no_search_result }}
    span(slot="noOptions")
      i {{ notifies.no_options_list }}

    template(slot="afterList")
      div(v-if="optObject && optObject.count && hasNextPage()", style="text-align: center")
        div(v-observe-visibility="reachedEndOfList")
        span(style="padding: 10px") ...

  q-icon.cancel-select-filter(name="cancel", v-show="isResettable", @click.stop="resetFilter()")
</template>

<script>
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";
import _ from "lodash";
import { normalizeSelectValue, sameSelectOptionValue } from "@/services/useFilterValues";

export default {
  components: {
    Multiselect,
  },

  props: {
    parentData: {
      type: Object,
      default: () => {},
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    selectionLabel: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      grid: this.parentData.grid,
      multiple: this.parentData.data[0].multiple || false,
      value: this.parentData.data[0].value,
      reset: this.parentData.data[0].reset === undefined ? true : this.parentData.data[0].reset,
      name: this.parentData.data[0].name,
      staticData: this.parentData.data[0].data,

      options_params: this.parentData.data[0].options_params || {},
      options_path: this.parentData.data[0].options_path,
      watch: this.parentData.data[0].watch || {},

      options_data: this.parentData.options_data,

      optObject: {},
      options: [],
      filterOptions: [],

      nextPage: 2,
      pageSize: 20,
      loading: false,
      error: false,
      parent: null,
    };
  },

  computed: {
    label() {
      return this.parentData.data[0].label;
    },

    currentFilter: {
      get() {
        return this.currentFilters[this.name];
      },
      set(value) {
        this.$store.commit("updateFilter", { grid_name: this.grid, filter: this.name, value: value });
        if (!value || (this.multiple && !value.length))
          this.$store.commit("resetFilter", { grid_name: this.grid, filter: this.name });
      },
    },

    isResettable() {
      if (!this.canCancel()) {
        // if default/value is not set
        return false;
      }

      return this.reset;
    },

    showSelectionLabel() {
      return this.selectionLabel && this.canCancel() ? true : false;
    },

    filterClass() {
      return {
        "select-filter--selection-label": this.showSelectionLabel,
      };
    },

    placeholder() {
      return this.showSelectionLabel ? "" : this.label;
    },

    valueExists() {
      return this.canCancel();
    },
  },

  beforeMount() {
    this.loadDefaultOptions();
  },

  mounted() {
    this.watchFilterParents();
  },

  methods: {
    canCancel(val = this.currentFilter) {
      if (this.multiple) {
        return val && val.length > 0;
      } else {
        return val && val.value !== "";
      }
    },

    emitLoadOptions(params, filter_name) {
      const options = { params: params, filter_name: filter_name, static_data: this.staticData };
      // By default currentFilter will contain default value (like number) or be undefined
      // When value is set it's a plain object
      if (!_.isPlainObject(this.currentFilter)) {
        options["default_value"] = this.value;
      }
      this.$emit("load-options", options);
    },

    watchParents() {
      if (this.watch && this.watch["parents"] && this.watch["parents"].length > 0) {
        return this.watch["parents"];
      }

      if (this.watch && this.watch["parent"]) {
        return [{ parent: this.watch["parent"], dependencies: [] }];
      }

      return [];
    },

    valueIsObjectOrNot(value) {
      if (Array.isArray(value)) {
        return value.map(el => (_.isPlainObject(el) ? el.value : el));
      }

      if (_.isPlainObject(value)) {
        return value.value;
      }

      return value;
    },

    valueHasData(value) {
      if (Array.isArray(value)) {
        return value.length > 0;
      }

      return value !== null && value !== undefined && value !== "";
    },

    filterValue(filterName) {
      return this.valueIsObjectOrNot(this.currentFilters[filterName]);
    },

    setWatchParam(params, paramName, value) {
      if (this.valueHasData(value)) {
        params[paramName] = value;
      } else {
        delete params[paramName];
      }
    },

    optionsParamsWithParents(parentValues = {}) {
      let params = Object.keys(this.options_params).reduce((result, key) => {
        result[key] = this.options_params[key];
        return result;
      }, {});

      this.watchParents().forEach(el => {
        const parentName = el.parent;
        const parentValue = Object.prototype.hasOwnProperty.call(parentValues, parentName)
          ? parentValues[parentName]
          : this.filterValue(parentName);

        this.setWatchParam(params, parentName, parentValue);

        let dependencies = el["dependencies"];
        if (dependencies && dependencies.length > 0) {
          dependencies.forEach(dependency => {
            this.setWatchParam(params, dependency, this.filterValue(dependency));
          });
        }
      });

      return params;
    },

    resetOptionsParamsPagination() {
      this.options_params["infinite_scroll"] = {
        page: 1,
        per_page: this.pageSize,
      };

      this.nextPage = 2;
    },

    watchFilterParents() {
      this.watchParents().forEach(el => {
        this.$watch(
          () => this.currentFilters[el.parent],
          (newValue, oldValue) => {
            let oldVal = this.valueIsObjectOrNot(oldValue);
            let newVal = this.valueIsObjectOrNot(newValue);

            if (_.isEqual(newVal, oldVal)) {
              return;
            }

            this.resetOptionsParamsPagination();
            this.options = [];
            this.parent = newVal;

            if (this.valueHasData(newVal)) {
              this.emitLoadOptions(this.optionsParamsWithParents({ [el.parent]: newVal }), this.name);
            } else if (this.valueHasData(oldVal)) {
              this.resetFilter();
              this.parent = null;
              this.loadDefaultOptions();
            }
          },
        );
      });
    },

    loadDefaultOptions(params = {}) {
      if (this.options_params) {
        if (this.options_params["infinite_scroll"]) {
          this.resetOptionsParamsPagination();
        }
        params = this.optionsParamsWithParents();
      }

      this.emitLoadOptions(params, this.name);
    },

    resetFilter() {
      this.$store.commit("resetFilter", { grid_name: this.grid, filter: this.name });
    },

    setOptionsData(data) {
      // КОСТЫЛЬ -- в этой компоненте частенько бывают запросы на вторую страницы пагинации, когда данных на ней нет.
      // В таких случаях бек возвращает пустую коллекцию. В некоторых случаях такой запрос удаляет уже выбранные значения.
      //
      // Пример -- фильтр "Услуга" (work_category_id) для новой страницы заявок для мвм (выбрать одно значение и потом
      // вбить в поисе одну букву, например "w", воспроизводится не каждый раз, но довольно часто).
      // Пока что не получилось найти корень проблемы (но он видимо связан с неправильным триггером
      // v-observe-visibility="reachedEndOfList")
      //
      // Поэтому сделана обертка, которая не дает перезаписать данные, когда мы на второй странице и пришли данные
      if (this.nextPage > 2 && data.opt_object.options.length === 0) {
        return;
      }

      this.optObject = data.opt_object;
      data.options.forEach(item => {
        let i = this.options.findIndex(opt => {
          return opt.value === item.value;
        });
        if (i < 0) {
          this.options.push(item);
        }
      });
      this.filterOptions = this.options;

      if (this.currentFilter) {
        if (this.multiple) {
          if (!this.currentFilter.includes(undefined)) {
            const val = this.currentFilter.map(el => normalizeSelectValue(el.value));

            if (val.length > 0) {
              val.forEach(id => {
                if (!this.options.some(option => sameSelectOptionValue(option.value, id))) {
                  this.currentFilter = this.currentFilter.filter(el => !sameSelectOptionValue(el.value, id));
                }
              });
            }
          } else {
            this.resetFilter();
          }
        } else {
          if (!this.options.some(option => sameSelectOptionValue(option.value, this.currentFilter.value))) {
            this.resetFilter();
          }
        }
      } else {
        this.resetFilter();
      }
    },

    setLoading(val) {
      this.loading = val;
    },

    setError(val) {
      this.error = val;
    },

    reachedEndOfList(reached) {
      if (reached && !this.error && !this.loading) {
        this.loading = true;

        this.$nextTick(() => {
          this.options_params["infinite_scroll"] = {
            page: this.nextPage,
            per_page: this.pageSize,
          };

          let attr = this.optionsParamsWithParents();

          this.nextPage++;
          this.emitLoadOptions(attr, this.name);
        });
      }
    },

    hasNextPage() {
      let lastPage = Math.ceil(this.optObject.count / this.pageSize);
      return this.nextPage <= lastPage;
    },

    searchFn: _.debounce(function (query) {
      let search_query = query.length > 0 ? query : null;
      this.options_params["search_query"] = search_query;

      this.options_params["infinite_scroll"] = {
        page: 1,
        per_page: this.pageSize,
      };

      this.nextPage = 2;

      this.options = [];
      this.emitLoadOptions(this.optionsParamsWithParents(), this.name);
    }, 500),
  },
};
</script>

<style scoped lang="scss">
@import "../../../assets/styles/filters/select";

.select-filter {
  cursor: pointer;

  &--selection-label {
    position: relative;

    .select-filter__label {
      position: absolute;
      left: 15px;
      font-size: 11px;
      color: var(--field-input-color);
    }

    :deep(.multiselect__tags) {
      position: relative;
      top: 5px;
      padding-top: 13px !important;

      .multiselect__tag {
        margin-bottom: 0;
        max-width: 150px;
      }
    }
  }
}
</style>
